import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { LinkInfoCircuit } from "../../wizard/steps/4-links/links-step.model";
import {
    CircuitDirection,
    CircuitElementState,
    LinkElement
} from "../models/diagram.models";
import { DwdmDiagramConverterHelper } from "./dwdm-diagram-converter-helper";
import {
    EndpointElementIdentifier,
    IntermediateElementIdentifier,
    LinkPoint,
    LinksConverterData
} from "./dwdm-diagram-converter.models";

/**
 * Classe abstrata para realizar a conversão de @see LinksConverterData em @see LinkElement.
 *
 * Essa classe contém todos os comportamentos comuns entre todas as janelas.
 * Comportamentos específicos da tela de Diagnósticos ou da Wizard de criação devem ser
 * implementados nas classes concretas.
 */
export abstract class DwdmDiagramAbstractConverterLinks {

    protected convertData(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
            zEndIdentifier: EndpointElementIdentifier, intermediates: IntermediateElementIdentifier[],
            customElementsConverter: (linkElement: LinkElement, link: LinkInfoCircuit) => void): LinkElement[] {

        if (this.shouldCreateDefaultLink(linksData)) {
            return this.buildDefaultLink(linksData, aEndIdentifier, zEndIdentifier);
        }

        const links = linksData.links;
        const allDevices: LinkPoint[] = this.getAllDevices(aEndIdentifier, zEndIdentifier, intermediates);

        const linkElements: LinkElement[] = [];
        for (const link of links) {
            const { source, target, identifier, direction } = this
                .getDataToConverter(allDevices, link, aEndIdentifier, zEndIdentifier, intermediates);

            const elementState = CircuitElementState.CONFIGURED;
            const canRemove = false;
            const linkElement = this.buildLink(source, target, direction, elementState, canRemove, identifier);

            customElementsConverter(linkElement, link);

            linkElements.push(linkElement);
        }

        return linkElements;
    }

    protected abstract buildDefaultLink(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
        zEndIdentifier: EndpointElementIdentifier): LinkElement[];

    protected abstract shouldCreateDefaultLink(linksData: LinksConverterData):boolean;

    protected buildLink(source: LinkPoint, target: LinkPoint, direction: CircuitDirection,
            state: CircuitElementState, canRemove: boolean, linkName?: string): LinkElement {

        return {
            id: DwdmDiagramConverterHelper.createId(),
            source: { deviceIdentifier: source.id, transmission: source.transmission },
            target: { deviceIdentifier: target.id, transmission: target.transmission },
            direction,
            canRemove,
            state,
            name: linkName
        };
    }

    private getDataToConverter(allDevices: LinkPoint[], link: LinkInfoCircuit,
            aEndIdentifier: EndpointElementIdentifier, zEndIdentifier: EndpointElementIdentifier,
            intermediatesIdentifier: IntermediateElementIdentifier[]) {

        const source: LinkPoint = this.getDeviceLinkPoint(allDevices, DwdmDiagramConverterHelper.getSourceDevice(link));
        const target: LinkPoint = this.getDeviceLinkPoint(allDevices, DwdmDiagramConverterHelper.getTargetDevice(link));

        const circuitAEndResourceId = aEndIdentifier.deviceResourceId;
        const circuitZEndResourceId = zEndIdentifier.deviceResourceId;

        const intermediates = intermediatesIdentifier.map((intermediate) => ({ resourceId: intermediate.deviceResourceId }));
        const direction = DwdmDiagramConverterHelper
            .getLinkDirection(link, circuitAEndResourceId, circuitZEndResourceId, intermediates);
        const identifier = link.identifier;

        // No contexto do circuito, a origem de um link sempre é TX e destino sempre é RX
        source.transmission = Transmission.TX;
        target.transmission = Transmission.RX;

        return { source, target, identifier, direction };
    }

    private getDeviceLinkPoint(allDevices: LinkPoint[], sourceDevice: number) {
        return allDevices.find((device) => sourceDevice === device.deviceResourceId);
    }

    private getAllDevices(aEndIdentifier: EndpointElementIdentifier, zEndIdentifier: EndpointElementIdentifier,
            intermediatesIdentifier: IntermediateElementIdentifier[]): LinkPoint[] {
        const aEndDeviceResourceId = aEndIdentifier.deviceResourceId;
        const aEndCircuitEndpoint = aEndIdentifier.endpoint;
        const zEndDeviceResourceId = zEndIdentifier.deviceResourceId;
        const zEndCircuitEndpoint = zEndIdentifier.endpoint;

        const endDevices: LinkPoint[] = [
            { id: aEndCircuitEndpoint.id, name: aEndCircuitEndpoint.name, deviceResourceId: aEndDeviceResourceId },
            { id: zEndCircuitEndpoint.id, name: zEndCircuitEndpoint.name, deviceResourceId: zEndDeviceResourceId }
        ];
        const intermediateDevices: LinkPoint[] = intermediatesIdentifier
            .map((intermediateIdentifier) => ({
                id: intermediateIdentifier.intermediate.id,
                name: intermediateIdentifier.intermediate.name,
                deviceResourceId: intermediateIdentifier.deviceResourceId
            }));

        return [...endDevices, ...intermediateDevices];
    }
}
